/**
 * Helpers for rendering the responsible user, accounting for active
 * substitutions.
 *
 * The backend keeps the responsible user pointing at the originally assigned
 * person (A) and pulls the active substitute's name along on the same record
 * via `substituteFirstName` / `substituteLastName` (flattened nightly, set
 * when `substituteUserId` is present). While substituted, the UI shows
 * "B (A)" — substitute first, original in parentheses.
 *
 * Everything here operates on a plain user-shaped object, so it works the same
 * whether the user came from a nested `responsibleUser` field or a lookup
 * against a users list.
 */

import type { User } from "@/types";

type NamedUser = Pick<User, "firstName" | "lastName"> & { email?: string | null };
type SubstitutableUser = NamedUser &
  Pick<User, "substituteUserId" | "substituteFirstName" | "substituteLastName">;

/** Display name for a single user — "First Last", falling back to email. */
export function userName(user: NamedUser): string {
  const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return name || user.email || "";
}

/** Two-letter initials from a first/last name pair, for an avatar fallback. */
export function initials(firstName?: string | null, lastName?: string | null): string {
  return (firstName?.charAt(0) ?? "") + (lastName?.charAt(0) ?? "");
}

/** Whether this user currently has an active substitute. */
export function isSubstituted(user?: SubstitutableUser | null): boolean {
  return !!user?.substituteUserId;
}

/** The substitute's display name ("First Last"), or null when not substituted. */
export function substituteName(user?: SubstitutableUser | null): string | null {
  if (!isSubstituted(user)) return null;
  const name = `${user!.substituteFirstName ?? ""} ${user!.substituteLastName ?? ""}`.trim();
  return name || null;
}

/**
 * Render the responsible user, showing the active substitute when present:
 *   - substituted:  "B (A)"  (B = substitute, A = original)
 *   - otherwise:    "A"
 * Returns an empty string when there is no responsible user.
 */
export function formatResponsible(user?: SubstitutableUser | null): string {
  if (!user) return "";
  const sub = substituteName(user);
  return sub ? `${sub} (${userName(user)})` : userName(user);
}

/**
 * Initials for the user who should be shown front-and-centre (avatar): the
 * active substitute when there is one, otherwise the original responsible user.
 */
export function effectiveInitials(user?: SubstitutableUser | null): string {
  if (!user) return "";
  if (isSubstituted(user)) {
    return initials(user.substituteFirstName, user.substituteLastName);
  }
  return initials(user.firstName, user.lastName);
}
